import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { getDatabase, ref, get, set } from "firebase/database";
import { toast } from "../toast";
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Editor, Transforms, Element as SlateElement } from 'slate';
import { withHistory } from 'slate-history';


const GroupCreate = () => {
  const [user, setUser] = useState(null);
  const [editor] = useState(() => withHistory(withReact(createEditor())));
  const [editorValue, setEditorValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ]);

  // Toggle block formatting
  const toggleBlock = (format) => {
    const isActive = isBlockActive(editor, format);
    const isList = ['bulleted-list', 'numbered-list'].includes(format);

    Transforms.unwrapNodes(editor, {
      match: n => ['bulleted-list', 'numbered-list'].includes(SlateElement.isElement(n) ? n.type : ''),
      split: true,
    });

    let newProperties;
    if (isList) {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    } else {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    }
    Transforms.setNodes(editor, newProperties);
  };

  // Toggle inline formatting
  const toggleMark = (format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const isBlockActive = (editor, format) => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Editor.nodes(editor, {
      match: n => SlateElement.isElement(n) && n.type === format,
    });
    return !!match;
  };

  const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const BlockButton = ({ format, label }) => {
    const isActive = isBlockActive(editor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleBlock(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}

        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const MarkButton = ({ format, label }) => {
    const isActive = isMarkActive(editor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleMark(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const [groupType, setGroupType] = useState("");  // Group Type

  // Handle group type change
  const handleGroupTypeChange = (e) => {
    setGroupType(e.target.value);

  };


  useEffect(() => {
    // Check for Bootstrap availability
    if (!window.bootstrap) {
      console.error("Bootstrap JS not loaded. Ensure the CDN is included in index.html.");
    }

    // Firebase Auth listener
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const [groupNameError, setGroupNameError] = useState("");

  const handleSaveGroup = () => {
    const database = getDatabase();

    onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const uid = user.uid;
      const userRef = ref(database, "USERS/" + uid);

      try {
        const snapshot = await get(userRef);
        if (!snapshot.exists()) {
          console.error("User data not found.");
          return;
        }

        const userData = snapshot.val();
        const GROUP_OWNER_NAME = userData.USERDISPLAYNAME || "Unknown";

        const groupName = document.getElementById("GroupName")?.value;
        if (!groupName || groupName.trim() === "") {
          setGroupNameError("Group name is required.");
          return;
        }

        // Collect group data
        const GROUP_NAME = groupName;
        const GROUP_DESC = JSON.stringify(editorValue);
        const GROUP_OWNER = uid;
        const GROUP_VERIFIED = "N";

        const GROUP_TYPE = document.getElementById("GroupType")?.value || "OTHER";
        const GROUP_PUBLIC = document.getElementById("GroupPublic")?.value || "N";
        const GROUP_JOINONLY = document.getElementById("GroupJoinOnly")?.value || "N";

        const slugify = (text) => {
          return text
            .toLowerCase()                   // lowercase
            .normalize("NFD")                // normalize accented characters
            .replace(/[\u0300-\u036f]/g, "")// remove accents
            .replace(/[^a-z0-9]+/g, "-")    // replace non-alphanumeric with -
            .replace(/(^-|-$)/g, "");       // trim - from start/end
        };

        const GROUP_SLUG = slugify(groupName);

        // Check if slug already exists
        const groupsRef = ref(database, "GROUPS");
        const groupsSnapshot = await get(groupsRef);
        
        if (groupsSnapshot.exists()) {
          const allGroups = groupsSnapshot.val();
          
          // Search through all groups to find duplicate slug
          for (const groupId in allGroups) {
            if (allGroups[groupId].GROUP_SLUG && allGroups[groupId].GROUP_SLUG === GROUP_SLUG) {
              toast("A group with this name already exists. Please choose a different name.", "#", "danger");
              return;
            }
          }
        }

        // Create unique ID
        const now = new Date();
        const groupCreateTime = `GROUPUID${now.getTime()}${Math.floor(Math.random() * 999999)}`;
        
        const newday = now.getDate();
        const newmonth = now.getMonth() + 1;
        const newyear = now.getFullYear();
        const hour = now.getHours();
        const GROUP_CREATED = `${newday}/${newmonth}/${newyear} ${hour}`;

        const groupData = {
          GROUP_NAME,
          GROUP_OWNER,
          GROUP_OWNER_NAME,
          GROUP_DESC,
          GROUP_VERIFIED,
          GROUP_TYPE,
          GROUP_SLUG,
          GROUP_PUBLIC,
          GROUP_JOINONLY,
          GROUP_CREATED,
          MEMBERS: {
            [uid]: {
              UID: uid,
              GROUP_RANK: "OWNER",
              JOINED_DATE: GROUP_CREATED,
            }
          }
        };

        // Save to DB
        const groupPath = `GROUPS/${groupCreateTime}`;
        await set(ref(database, groupPath), groupData);

        // Add group reference to user's groups
        const userGroupPath = `USERS/${uid}/GROUPS/${groupCreateTime}/${GROUP_SLUG}`;
        await set(ref(database, userGroupPath), {
          GROUP_ID: groupCreateTime,
          GROUP_NAME,
          GROUP_SLUG,
          GROUP_RANK: "OWNER",
          JOINED_DATE: GROUP_CREATED,
        });

        // Hide modal and show success toast
        toast("Your group has been successfully created!", `https://attendiau.co.uk/group/${GROUP_SLUG}`, "created");

      } catch (error) {
        // console.log(error);
        toast("Error creating Group. <br> Please Retry.", "#", "danger");
      }
    });
  };

  //   const handleClick = () => {
  //       toast("Event Created!", "success");
  //   };

  return (
    <div className="container my-5">

      {user && (
        <>
          
        <div className="row">
          <div className="col-md-6 mb-3">
        <label htmlFor="GroupType" className="form-label">Group Type</label>
        <select
          className="form-select"
          id="GroupType"
          value={groupType}
          onChange={handleGroupTypeChange}
        >
          <option value="">Select</option>
          <option value="AUTOMOTIVE">Automotive</option>
          <option value="SPORTS">Sports</option>
          <option value="MUSIC">Music</option>
          <option value="OTHER">Other</option>
        </select>
       </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="GroupName" className="form-label">
              Group Name <span className="text-danger-small">Required</span>
            </label>
            <input type="text" className={`form-control${groupNameError ? " is-invalid" : ""}`} id="GroupName" />
            {groupNameError && <div className="text-danger mt-1">{groupNameError}</div>}
          </div>
        </div>

        <div className="row ">
          <div className="col-12 mb-3">
            <label className="form-label">Description</label>
            <div className="editor-wrapper">
              {/* Toolbar */}
              <div className="editor-toolbar">
                <MarkButton  format="bold" label='<i class="bi bi-type-bold"></i>' />
                <MarkButton  format="italic" label='<i class="bi bi-type-italic"></i>' />
                <MarkButton  format="underline" label='<i class="bi bi-type-underline"></i>' />
                <BlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                <BlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                <BlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                <BlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                <BlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                <BlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
              </div>
              {/* Editor */}
              <div className="editor-container">
                <Slate editor={editor} initialValue={editorValue} onChange={setEditorValue}>
                  <Editable 
                    placeholder="Type your group description here..."
                    style={{ outline: 'none', minHeight: '120px', fontFamily: 'inherit', fontSize: 'inherit' }}
                    renderElement={({ attributes, children, element }) => {
                      switch (element.type) {
                        case 'heading-one':
                          return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h1>;
                        case 'heading-two':
                          return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h2>;
                        case 'heading-three':
                          return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0'}}>{children}</h3>;
                        case 'bulleted-list':
                          return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                        case 'numbered-list':
                          return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                        default:
                          return <p {...attributes}>{children}</p>;
                      }
                    }}
                    renderLeaf={({ attributes, children, leaf }) => {
                      if (leaf.bold) children = <strong>{children}</strong>;
                      if (leaf.italic) children = <em>{children}</em>;
                      if (leaf.underline) children = <u>{children}</u>;
                      return <span {...attributes}>{children}</span>;
                    }}
                  />
                </Slate>
              </div>
            </div>
          </div>
        </div>

        <div className="row"> 
          <div className="col-md-6 mb-3">
            <label htmlFor="GroupPublic" className="form-label publicselect">Public Group </label>
            <select className="form-select" id="GroupPublic">
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>
        </div>      
   

        <div className="row"> 
          <div className="col-md-6 mb-3">
            <label htmlFor="GroupJoinOnly" className="form-label publicselect"> Users have to request to join?</label>
            <select className="form-select" id="GroupJoinOnly">
              <option value="N">No</option>
              <option value="Y">Yes</option>
            </select>
          </div>
        </div>      

        <div className="row">
          <div className="col-md-12 mb-3 text-end">
            <button type="button" className="btn btn-primary mt-3" onClick={handleSaveGroup}>Create</button>
          </div>
        </div>
          
        </>
      )}

    </div>
  );
};

export default GroupCreate;
